import type { CSSProperties } from 'react';
import { LIST_COLOURS, type ListColour } from '@molt/shared';

export interface ListColourOption {
  readonly value: ListColour;
  readonly label: string;
  readonly style: CSSProperties;
}

export type ListColourTranslate = (key: string) => string;

const LABEL_KEYS: Record<ListColour, string> = {
  none: 'primitives.listColour.none',
  subtle_green: 'primitives.listColour.subtleGreen',
  subtle_yellow: 'primitives.listColour.subtleYellow',
  subtle_orange: 'primitives.listColour.subtleOrange',
  subtle_red: 'primitives.listColour.subtleRed',
  subtle_purple: 'primitives.listColour.subtlePurple',
  subtle_blue: 'primitives.listColour.subtleBlue',
  subtle_sky: 'primitives.listColour.subtleSky',
  subtle_lime: 'primitives.listColour.subtleLime',
  subtle_pink: 'primitives.listColour.subtlePink',
  subtle_grey: 'primitives.listColour.subtleGrey',
};

const CSS_VARS: Partial<Record<ListColour, string>> = {
  subtle_green: '--list-colour-subtle-green',
  subtle_yellow: '--list-colour-subtle-yellow',
  subtle_orange: '--list-colour-subtle-orange',
  subtle_red: '--list-colour-subtle-red',
  subtle_purple: '--list-colour-subtle-purple',
  subtle_blue: '--list-colour-subtle-blue',
  subtle_sky: '--list-colour-subtle-sky',
  subtle_lime: '--list-colour-subtle-lime',
  subtle_pink: '--list-colour-subtle-pink',
  subtle_grey: '--list-colour-subtle-grey',
};

export function listColourLabel(t: ListColourTranslate, colour: ListColour): string {
  return t(LABEL_KEYS[colour]);
}

/** The list colour's own custom property (`var(--list-colour-…)`); `none` has none. */
export function listColourVar(colour: ListColour): string | undefined {
  const cssVar = CSS_VARS[colour];
  return cssVar === undefined ? undefined : `var(${cssVar})`;
}

export function listColourStyle(colour: ListColour): CSSProperties | undefined {
  const cssVar = CSS_VARS[colour];
  if (cssVar === undefined) return undefined;
  return {
    backgroundColor: `color-mix(in oklab, var(${cssVar}) 82%, var(--color-panel))`,
    borderColor: `color-mix(in oklab, var(${cssVar}) 48%, var(--color-border-strong))`,
  };
}

function option(t: ListColourTranslate, colour: ListColour): ListColourOption {
  return {
    value: colour,
    label: listColourLabel(t, colour),
    style: listColourStyle(colour) ?? {},
  };
}

export function listColourOptions(t: ListColourTranslate): readonly ListColourOption[] {
  return LIST_COLOURS.map((colour) => option(t, colour));
}
